import {
  Ref,
  ref,
} from 'vue';

const DEFAULT_LIMIT = 5;

class KeyedConcurrency {
  activeCount: Ref<number> = ref(0);

  queue: (() => void)[] = [];

  limit: number;

  constructor(limit: number) {
    this.limit = limit;
  }
}

class ConcurrencyManager {
  private keyedMap = new Map<string, KeyedConcurrency>();

  setLimit(key: string, limit: number) {
    const entry = this.keyedMap.get(key);
    if (entry) {
      entry.limit = limit;
    } else {
      this.keyedMap.set(key, new KeyedConcurrency(limit));
    }
  }

  async acquire(key: string): Promise<void> {
    const entry = this.getOrCreate(key);
    if (entry.activeCount.value >= entry.limit) {
      await new Promise<void>((resolve) => {
        entry.queue.push(resolve);
      });
    }
    entry.activeCount.value++;
  }

  release(key: string): void {
    const entry = this.getOrCreate(key);
    if (entry.activeCount.value === 0) {
      return;
    }
    entry.activeCount.value--;
    const next = entry.queue.shift();
    if (next) {
      next();
    }
  }

  async withLock<T>(key: string, fn: () => Promise<T>): Promise<T> {
    await this.acquire(key);
    try {
      return await fn();
    } finally {
      this.release(key);
    }
  }

  clearQueue(key: string): void {
    this.getOrCreate(key).queue = [];
  }

  getActiveCount(key: string): Ref<number> {
    return this.getOrCreate(key).activeCount;
  }

  getQueueLength(key: string): number {
    return this.getOrCreate(key).queue.length;
  }

  private getOrCreate(key: string): KeyedConcurrency {
    if (!this.keyedMap.has(key)) {
      this.keyedMap.set(key, new KeyedConcurrency(DEFAULT_LIMIT));
    }
    return this.keyedMap.get(key)!;
  }
}

export const globalConcurrencyManager = new ConcurrencyManager();
